import type { LegalDocument } from '../types';
import { TERMINI_VERSIONE_CORRENTE } from '../currentVersions';

export const TERMINI_IT: LegalDocument = {
  kind: 'termini_servizio',
  titolo: 'Termini di servizio',
  sottotitolo: 'Marketplace FV — Piattaforma B2B per il fotovoltaico italiano',
  versione: TERMINI_VERSIONE_CORRENTE,
  data_ultima_modifica: '2026-05-16',
  data_efficacia: '2026-05-16',
  in_preparazione: true,
  introduzione: [
    {
      type: 'text',
      text: 'I presenti termini regolano l\'accesso e l\'utilizzo della piattaforma Marketplace FV (di seguito "Piattaforma" o "Servizio") da parte delle aziende che si iscrivono come venditori, installatori o entrambi. Iscrivendoti dichiari di aver letto, compreso e accettato i presenti termini integralmente.',
    },
    {
      type: 'note',
      text: '**Documento in fase di preparazione.** I testi definitivi sono in fase di redazione legale e saranno pubblicati prima dell\'apertura ufficiale del marketplace al pubblico. La struttura del documento, le sezioni e gli ambiti regolati sono quelli definitivi: cambieranno solo i contenuti specifici di ogni sezione.',
    },
  ],
  sezioni: [
    {
      id: '1-definizioni',
      numero: '1',
      titolo: 'Definizioni',
      paragrafi: [
        {
          type: 'text',
          text: 'Ai fini dei presenti termini si intende per:',
        },
        {
          type: 'definition',
          term: 'Piattaforma',
          definition: 'l\'applicazione web Marketplace FV accessibile dalla landing pubblica e dalla web app, che mette in contatto venditori e installatori di impianti fotovoltaici sul territorio italiano.',
        },
        {
          type: 'definition',
          term: 'Tenant',
          definition: 'l\'azienda iscritta alla Piattaforma, identificata univocamente dal proprio account aziendale e dalla propria Partita IVA.',
        },
        {
          type: 'definition',
          term: 'Venditore',
          definition: 'il Tenant che pubblica richieste di installazione di impianti fotovoltaici.',
        },
        {
          type: 'definition',
          term: 'Installatore',
          definition: 'il Tenant che riceve e risponde a richieste di installazione, eseguendo il lavoro presso il cliente finale del Venditore.',
        },
        {
          type: 'definition',
          term: 'Cliente finale',
          definition: 'la persona fisica o giuridica destinataria dell\'installazione fotovoltaica, soggetto terzo rispetto al rapporto tra Venditore e Installatore.',
        },
        {
          type: 'definition',
          term: 'Match',
          definition: 'l\'accettazione formale di un\'offerta da parte di un Venditore, che genera un rapporto di partnership operativa tra Venditore e Installatore.',
        },
      ],
    },
    {
      id: '2-oggetto',
      numero: '2',
      titolo: 'Oggetto del servizio',
      paragrafi: [
        {
          type: 'text',
          text: '[CONTENUTO IN PREPARAZIONE — questa sezione descrivera in dettaglio: cosa fa la Piattaforma, quali servizi offre ai Venditori e agli Installatori, le modalita di funzionamento del marketplace, l\'eventuale fee applicata sulle transazioni, le tipologie di account (tier) disponibili, l\'estensione geografica del servizio (Italia).]',
        },
      ],
    },
    {
      id: '3-iscrizione',
      numero: '3',
      titolo: 'Modalità di iscrizione e attivazione account',
      paragrafi: [
        {
          type: 'text',
          text: '[CONTENUTO IN PREPARAZIONE — descrivera: requisiti per l\'iscrizione (Partita IVA italiana o europea valida, sede legale in UE), il processo di KYC tramite verifica P.IVA su VIES, l\'obbligo di verifica email prima del primo accesso, la possibilita di iscrizione tramite Email/Password o Google, l\'attivazione iniziale in modalita "active_limited" con sandbox di 30 giorni.]',
        },
      ],
      sottosezioni: [
        {
          id: '3-1-account-amministratore',
          titolo: 'Account amministratore',
          paragrafi: [
            {
              type: 'text',
              text: '[CONTENUTO IN PREPARAZIONE — descrivera ruolo e responsabilita dell\'utente che effettua l\'iscrizione (admin del Tenant), capacita di invitare ulteriori utenti dell\'organizzazione in fasi successive.]',
            },
          ],
        },
        {
          id: '3-2-veridicita-dati',
          titolo: 'Veridicità dei dati forniti',
          paragrafi: [
            {
              type: 'text',
              text: '[CONTENUTO IN PREPARAZIONE — l\'utente garantisce la veridicita dei dati forniti in fase di iscrizione. Conseguenze in caso di dichiarazioni false: sospensione, ban, eventuali azioni legali.]',
            },
          ],
        },
      ],
    },
    {
      id: '4-obblighi-utente',
      numero: '4',
      titolo: 'Obblighi e responsabilità dell\'utente',
      paragrafi: [
        {
          type: 'text',
          text: '[CONTENUTO IN PREPARAZIONE — questa sezione elenchera gli obblighi specifici per Venditori e Installatori, il divieto di disintermediazione (contattare la controparte fuori dalla Piattaforma per evitare fee), il divieto di pubblicare informazioni false o ingannevoli, gli obblighi di rispetto delle normative fiscali e contrattuali nei rapporti con i clienti finali.]',
        },
      ],
    },
    {
      id: '5-tariffe-pagamenti',
      numero: '5',
      titolo: 'Tariffe e modalità di pagamento',
      paragrafi: [
        {
          type: 'text',
          text: '[CONTENUTO IN PREPARAZIONE — periodo di prova gratuito (30 giorni di default), tariffazione successiva (canone mensile per Tenant attivi), modalita di pagamento (carta di credito o SEPA tramite Stripe), fatturazione, sospensione in caso di mancato pagamento, eventuali fee marketplace su transazioni in fasi successive.]',
        },
      ],
    },
    {
      id: '6-limitazioni-responsabilita',
      numero: '6',
      titolo: 'Limitazioni di responsabilità',
      paragrafi: [
        {
          type: 'text',
          text: '[CONTENUTO IN PREPARAZIONE — la Piattaforma e un facilitatore di incontro tra domanda e offerta. Marketplace FV non e parte dei contratti tra Venditore e Installatore, ne dei contratti con i clienti finali. Limitazioni di responsabilita per malfunzionamenti tecnici, downtime, perdita dati. Esclusione di responsabilita per la qualita del lavoro eseguito dagli Installatori e per le scelte commerciali dei Venditori.]',
        },
      ],
    },
    {
      id: '7-proprieta-intellettuale',
      numero: '7',
      titolo: 'Proprietà intellettuale',
      paragrafi: [
        {
          type: 'text',
          text: '[CONTENUTO IN PREPARAZIONE — diritti sulla Piattaforma (codice, design, marchi) di proprieta del titolare. Licenza d\'uso non esclusiva all\'utente. Diritti dell\'utente sui propri contenuti pubblicati (foto cantieri, descrizioni, listini). Concessione di una licenza limitata alla Piattaforma per visualizzare e indicizzare tali contenuti.]',
        },
      ],
    },
    {
      id: '8-recesso-sospensione',
      numero: '8',
      titolo: 'Recesso, sospensione e cessazione',
      paragrafi: [
        {
          type: 'text',
          text: '[CONTENUTO IN PREPARAZIONE — diritto di recesso dell\'utente in qualsiasi momento, modalita di richiesta cancellazione account, conservazione dati post-cancellazione (per obblighi fiscali e contabili). Diritto della Piattaforma di sospendere o cessare l\'account in caso di violazione dei termini.]',
        },
      ],
    },
    {
      id: '9-modifiche-servizio',
      numero: '9',
      titolo: 'Modifiche al servizio e ai termini',
      paragrafi: [
        {
          type: 'text',
          text: '[CONTENUTO IN PREPARAZIONE — la Piattaforma puo evolvere nelle funzionalita. Modifiche ai presenti termini saranno notificate via email e in app con preavviso minimo. L\'utente potra esercitare il diritto di recesso se non intende accettare le nuove condizioni.]',
        },
      ],
    },
    {
      id: '10-forza-maggiore',
      numero: '10',
      titolo: 'Forza maggiore',
      paragrafi: [
        {
          type: 'text',
          text: '[CONTENUTO IN PREPARAZIONE — esonero da responsabilita per eventi di forza maggiore (catastrofi naturali, atti governativi, attacchi informatici di terzi, malfunzionamenti di servizi terzi su cui la Piattaforma poggia).]',
        },
      ],
    },
    {
      id: '11-foro-competente',
      numero: '11',
      titolo: 'Foro competente e legge applicabile',
      paragrafi: [
        {
          type: 'text',
          text: '[CONTENUTO IN PREPARAZIONE — i presenti termini sono regolati dalla legge italiana. Per qualsiasi controversia sara competente in via esclusiva il Foro di [SEDE DA DEFINIRE].]',
        },
      ],
    },
    {
      id: '12-contatti',
      numero: '12',
      titolo: 'Contatti',
      paragrafi: [
        {
          type: 'text',
          text: '[CONTENUTO IN PREPARAZIONE — riferimenti per richieste, reclami, esercizio diritti contrattuali: indirizzo, email, PEC del titolare della Piattaforma.]',
        },
      ],
    },
  ],
  contatti_titolare: {
    ragione_sociale: '[Ragione sociale del titolare — DA DEFINIRE]',
    indirizzo: '[Indirizzo sede legale — DA DEFINIRE]',
    email: '[Email contatto — DA DEFINIRE]',
    pec: '[PEC — DA DEFINIRE]',
  },
};
